import { createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";

import {
  CURRENT_DEPLOYMENT_ID,
  NEXT_DEPLOYMENT_ID,
  isSettledDeploymentErrorCode
} from "./constants";
import {
  loadDeploymentCurrent,
  loadDeploymentNext,
  loadDeploymentSettings,
  loadDeploymentLocalDedicated,
  updateDeployment,
  updateDeploymentSettings,
  loadDeploymentCurrentImages
} from "./thunks";

import type { DeploymentState } from "./types";

const initialState: DeploymentState = {};

// Helper to create a unique key for tracking fetched environments
const getEnvironmentKey = (
  organizationId: string,
  projectId: string,
  environmentId: string
) => `${organizationId}/${projectId}/${environmentId}`;

const deploymentSlice = createSlice({
  name: "deployment",
  initialState,
  reducers: {},
  extraReducers: builder => {
    // LOAD CURRENT DEPLOYMENT
    builder
      .addCase(loadDeploymentCurrent.pending, state => {
        state.loading = true;
        state.loadingCurrent = true;
      })
      .addCase(loadDeploymentCurrent.fulfilled, (state, { payload, meta }) => {
        const { organizationId, projectId, environmentId } = meta.arg;

        if (payload?.schemas) {
          state.schemas = payload.schemas;
        }
        state.loading = false;
        state.loadingCurrent = false;

        if (payload?.deployment) {
          setDeep(
            state,
            [
              "data",
              organizationId,
              projectId,
              environmentId,
              CURRENT_DEPLOYMENT_ID
            ],
            payload.deployment
          );
        }

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          undefined
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          200
        );
        if (payload?.services) {
          setDeep(
            state,
            ["services", organizationId, projectId, environmentId],
            payload.services
          );
        }

        // Mark current deployment as fetched for this environment
        const envKey = getEnvironmentKey(
          organizationId,
          projectId,
          environmentId
        );
        state.fetchedEnvironments = state.fetchedEnvironments || {};
        state.fetchedEnvironments[envKey] = {
          ...state.fetchedEnvironments[envKey],
          current: true
        };
      })
      .addCase(loadDeploymentCurrent.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loading = false;
        state.loadingCurrent = false;

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          action.payload
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          // payload is undefined when the thunk throws before reaching
          // rejectWithValue — don't let the reducer crash on it.
          action.payload?.code
        );

        // Mark as fetched only when the API's answer is final — a 404 stays a
        // 404, but a 500 must be retryable or one blip wedges the panels for
        // the rest of the session (CON-1655).
        if (isSettledDeploymentErrorCode(action.payload?.code)) {
          const envKey = getEnvironmentKey(
            organizationId,
            projectId,
            environmentId
          );
          state.fetchedEnvironments = state.fetchedEnvironments || {};
          state.fetchedEnvironments[envKey] = {
            ...state.fetchedEnvironments[envKey],
            current: true
          };
        }
      })
      // LOAD NEXT DEPLOYMENT
      .addCase(loadDeploymentNext.pending, state => {
        state.loading = true;
        state.loadingNext = true;
      })
      .addCase(loadDeploymentNext.fulfilled, (state, { payload, meta }) => {
        const { organizationId, projectId, environmentId } = meta.arg;

        if (payload?.schemas) {
          state.schemas = payload.schemas;
        }
        state.loading = false;
        state.loadingNext = false;

        if (payload?.deployment) {
          setDeep(
            state,
            [
              "data",
              organizationId,
              projectId,
              environmentId,
              NEXT_DEPLOYMENT_ID
            ],
            payload.deployment
          );
        }

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          undefined
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          200
        );

        // Mark next deployment as fetched for this environment
        const envKey = getEnvironmentKey(
          organizationId,
          projectId,
          environmentId
        );
        state.fetchedEnvironments = state.fetchedEnvironments || {};
        state.fetchedEnvironments[envKey] = {
          ...state.fetchedEnvironments[envKey],
          next: true
        };
      })
      .addCase(loadDeploymentNext.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loading = false;
        state.loadingNext = false;

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          // payload is undefined when the thunk throws before reaching
          // rejectWithValue — don't let the reducer crash on it.
          action.payload?.code
        );

        // Final answers only; see loadDeploymentCurrent.rejected above.
        if (isSettledDeploymentErrorCode(action.payload?.code)) {
          const envKey = getEnvironmentKey(
            organizationId,
            projectId,
            environmentId
          );
          state.fetchedEnvironments = state.fetchedEnvironments || {};
          state.fetchedEnvironments[envKey] = {
            ...state.fetchedEnvironments[envKey],
            next: true
          };
        }
      })
      // LOAD DEPLOYMENT SETTINGS
      .addCase(loadDeploymentSettings.pending, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;
        state.loadingSettings = true;

        setDeep(
          state,
          ["errors", organizationId, projectId, environmentId, "settings"],
          undefined
        );
      })
      .addCase(loadDeploymentSettings.fulfilled, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = false;

        setDeep(
          state,
          ["settings", organizationId, projectId, environmentId],
          action.payload
        );
      })
      .addCase(loadDeploymentSettings.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = false;

        setDeep(
          state,
          ["errors", organizationId, projectId, environmentId, "settings"],
          action.payload
        );
        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            "settings"
          ],
          // Undefined when the thunk throws before reaching rejectWithValue,
          // as the current/next reducers already guard against. It matters
          // more now that isDeploymentSettingsFetchedSelector reads this.
          action.payload?.code
        );
      })
      // UPDATE DEPLOYMENT
      .addCase(updateDeployment.pending, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.updateLoading = true;

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          undefined
        );

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          undefined
        );
      })
      .addCase(updateDeployment.fulfilled, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.updateLoading = false;

        setDeep(
          state,
          [
            "data",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          200
        );
      })
      .addCase(updateDeployment.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.updateLoading = false;

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload.code
        );
      })
      // UPDATE DEPLOYMENT SETTINGS
      .addCase(updateDeploymentSettings.pending, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = true;

        setDeep(
          state,
          ["errors", organizationId, projectId, environmentId, "settings"],
          undefined
        );
      })
      .addCase(updateDeploymentSettings.fulfilled, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = false;

        setDeep(
          state,
          ["settings", organizationId, projectId, environmentId],
          action.payload
        );
      })
      .addCase(updateDeploymentSettings.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = false;

        setDeep(
          state,
          ["errors", organizationId, projectId, environmentId, "settings"],
          action.payload
        );
      })
      // LOAD LOCAL DEDICATED DEPLOYMENT
      .addCase(loadDeploymentLocalDedicated.pending, state => {
        state.loading = true;
        state.errors = state.errors || {};
        state.errors["local-dedicated"] = undefined;
      })
      .addCase(loadDeploymentLocalDedicated.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.data = state.data || {};
        state.data["local-dedicated"] = payload;
        state.errors = state.errors || {};
        state.errors["local-dedicated"] = undefined;
      })
      .addCase(loadDeploymentLocalDedicated.rejected, (state, action) => {
        state.loading = false;
        state.errors = state.errors || {};
        state.errors["local-dedicated"] = action.payload as {
          code: number;
          message: string;
        };
      }); // LOAD CURRENT DEPLOYMENT IMAGES
    builder
      .addCase(loadDeploymentCurrentImages.pending, () => {})
      .addCase(
        loadDeploymentCurrentImages.fulfilled,
        (state, { payload, meta }) => {
          const { projectId, environmentId } = meta.arg;
          if (payload) {
            setDeep(
              state,
              ["images", "data", projectId, environmentId],
              payload
            );
          }
          setDeep(state, ["images", "errors", projectId, environmentId], null);
        }
      )
      .addCase(loadDeploymentCurrentImages.rejected, (state, action) => {
        const { projectId, environmentId } = action.meta.arg;

        setDeep(
          state,
          ["images", "errors", projectId, environmentId],
          action.payload
        );
      });
  }
});

export default deploymentSlice.reducer;
